import React from "react";
import { motion } from "framer-motion";
import {
  FaMoneyBillWave,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaCreditCard,
  FaCalendarAlt,
  FaReceipt,
  FaDollarSign,
  FaTag,
  FaFileAlt,
  FaUser,
  FaIdCard,
  FaEdit,
  FaSave,
  FaTimes,
  FaPercentage,
  FaPauseCircle,
  FaAdjust,
} from "react-icons/fa";
import Txt from "../../../Text";
import { arClass } from "../../../../utils/useArClass";
import textTranslator from "../../../../utils/useTextTranslator";

const Text = ({ children, className, page = "PAYMENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const PaymentDetails = ({ payment, setPayment, darkMode }) => {
  const paymentOptions = [
    {
      label: textTranslator("cash", "PAYMENTS"),
      value: "cash",
      icon: FaMoneyBillWave,
    },
    {
      label: textTranslator("Credit Card", "PAYMENTS"),
      value: "credit-card",
      icon: FaCreditCard,
    },
    {
      label: textTranslator("Debit Card", "PAYMENTS"),
      value: "debit-card",
      icon: FaCreditCard,
    },
    {
      label: textTranslator("Insurance", "PAYMENTS"),
      value: "insurance",
      icon: FaUser,
    },
    {
      label: textTranslator("Online Payment", "PAYMENTS"),
      value: "online-payment",
      icon: FaReceipt,
    },
    {
      label: textTranslator("Other", "PAYMENTS"),
      value: "other",
      icon: FaFileAlt,
    },
  ];

  const paymentStatusOptions = [
    {
      label: textTranslator("Pending", "PAYMENTS"),
      value: "pending",
      icon: FaClock,
    },
    {
      label: textTranslator("Completed", "PAYMENTS"),
      value: "completed",
      icon: FaCheckCircle,
    },
    {
      label: textTranslator("Failed", "PAYMENTS"),
      value: "failed",
      icon: FaExclamationTriangle,
    },
    {
      label: textTranslator("Refunded", "PAYMENTS"),
      value: "refunded",
      icon: FaTimes,
    },
    {
      label: textTranslator("Cancelled", "PAYMENTS"),
      value: "cancelled",
      icon: FaTimes,
    },
    {
      label: textTranslator("On Hold", "PAYMENTS"),
      value: "on-hold",
      icon: FaPauseCircle,
    },
    {
      label: textTranslator("Partially Paid", "PAYMENTS"),
      value: "partially-paid",
      icon: FaAdjust,
    },
  ];

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        icon: FaClock,
        color: darkMode ? "text-yellow-400" : "text-yellow-600",
        bg: darkMode ? "bg-yellow-900/30" : "bg-yellow-100",
        border: darkMode ? "border-yellow-700/50" : "border-yellow-200",
        iconBg: darkMode ? "bg-yellow-600" : "bg-yellow-500",
        accent: darkMode ? "text-yellow-400" : "text-yellow-600",
        text: darkMode ? "text-yellow-300" : "text-yellow-700",
      },
      completed: {
        icon: FaCheckCircle,
        color: darkMode ? "text-green-400" : "text-green-600",
        bg: darkMode ? "bg-green-900/30" : "bg-green-100",
        border: darkMode ? "border-green-700/50" : "border-green-200",
        iconBg: darkMode ? "bg-green-600" : "bg-green-500",
        accent: darkMode ? "text-green-400" : "text-green-600",
        text: darkMode ? "text-green-300" : "text-green-700",
      },
      failed: {
        icon: FaExclamationTriangle,
        color: darkMode ? "text-red-400" : "text-red-600",
        bg: darkMode ? "bg-red-900/30" : "bg-red-100",
        border: darkMode ? "border-red-700/50" : "border-red-200",
        iconBg: darkMode ? "bg-red-600" : "bg-red-500",
        accent: darkMode ? "text-red-400" : "text-red-600",
        text: darkMode ? "text-red-300" : "text-red-700",
      },
      refunded: {
        icon: FaTimes,
        color: darkMode ? "text-purple-400" : "text-purple-600",
        bg: darkMode ? "bg-purple-900/30" : "bg-purple-100",
        border: darkMode ? "border-purple-700/50" : "border-purple-200",
        iconBg: darkMode ? "bg-purple-600" : "bg-purple-500",
        accent: darkMode ? "text-purple-400" : "text-purple-600",
        text: darkMode ? "text-purple-300" : "text-purple-700",
      },
      cancelled: {
        icon: FaTimes,
        color: darkMode ? "text-red-400" : "text-red-600",
        bg: darkMode ? "bg-red-900/30" : "bg-red-100",
        border: darkMode ? "border-red-700/50" : "border-red-200",
        iconBg: darkMode ? "bg-red-600" : "bg-red-500",
        accent: darkMode ? "text-red-400" : "text-red-600",
        text: darkMode ? "text-red-300" : "text-red-700",
      },
      "on-hold": {
        icon: FaPauseCircle,
        color: darkMode ? "text-yellow-400" : "text-yellow-600",
        bg: darkMode ? "bg-yellow-900/30" : "bg-yellow-100",
        border: darkMode ? "border-yellow-700/50" : "border-yellow-200",
        iconBg: darkMode ? "bg-yellow-600" : "bg-yellow-500",
        accent: darkMode ? "text-yellow-400" : "text-yellow-600",
        text: darkMode ? "text-yellow-300" : "text-yellow-700",
      },
      "partially-paid": {
        icon: FaAdjust,
        color: darkMode ? "text-blue-400" : "text-blue-600",
        bg: darkMode ? "bg-blue-900/30" : "bg-blue-100",
        border: darkMode ? "border-blue-700/50" : "border-blue-200",
        iconBg: darkMode ? "bg-blue-600" : "bg-blue-500",
        accent: darkMode ? "text-blue-400" : "text-blue-600",
        text: darkMode ? "text-blue-300" : "text-blue-700",
      },
    };
    return configs[status?.toLowerCase()] || configs.pending;
  };

  const getMethodConfig = (method) => {
    const configs = {
      cash: {
        icon: FaMoneyBillWave,
        color: darkMode ? "text-green-400" : "text-green-600",
        bg: darkMode ? "bg-green-900/30" : "bg-green-100",
      },
      "credit-card": {
        icon: FaCreditCard,
        color: darkMode ? "text-blue-400" : "text-blue-600",
        bg: darkMode ? "bg-blue-900/30" : "bg-blue-100",
      },
      "debit-card": {
        icon: FaCreditCard,
        color: darkMode ? "text-purple-400" : "text-purple-600",
        bg: darkMode ? "bg-purple-900/30" : "bg-purple-100",
      },
      insurance: {
        icon: FaUser,
        color: darkMode ? "text-orange-400" : "text-orange-600",
        bg: darkMode ? "bg-orange-900/30" : "bg-orange-100",
      },
      "online-payment": {
        icon: FaReceipt,
        color: darkMode ? "text-cyan-400" : "text-cyan-600",
        bg: darkMode ? "bg-cyan-900/30" : "bg-cyan-100",
      },
      other: {
        icon: FaFileAlt,
        color: darkMode ? "text-gray-400" : "text-gray-600",
        bg: darkMode ? "bg-gray-900/30" : "bg-gray-100",
      },
    };
    return configs[method?.toLowerCase()] || configs.cash;
  };

  const statusConfig = getStatusConfig(payment.paymentStatus);
  const StatusIcon = statusConfig.icon;
  const methodConfig = getMethodConfig(payment.method);
  const MethodIcon = methodConfig.icon;

  const amountAfterDiscount = (
    Number(payment.amount || 0) *
    (1 - Number(payment.discountPercent || 0) / 100)
  ).toFixed(2);

  return (
    <div className={arClass("space-y-6 max-sm:space-y-4")}>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="group"
      >
        <div
          className={arClass(
            `rounded-2xl  max-sm:rounded-xl transition-all duration-300 overflow-hidden`
          )}
        >
          <div
  className={arClass(
    "p-6 max-sm:p-4 flex gap-2 max-lg:flex-col w-full items-stretch"
  )}
>

            {/* Amount Section */}
            <motion.div
              whileHover={{ y: -5 }}
              className={arClass(
    `p-6 rounded-xl max-sm:p-4 max-sm:mb-4 w-full flex-1 ${
      darkMode
        ? "bg-slate-800/50 border border-slate-700/50"
        : "bg-white/50 border border-slate-200"
    } backdrop-blur-sm`
  )}
>
              <div
                className={arClass("flex items-center gap-3 mb-4 max-sm:mb-3")}
              >
                <FaDollarSign
                  className={arClass(
                    `w-5 h-5 max-sm:w-4 max-sm:h-4 ${statusConfig.accent}`
                  )}
                />
                <h4
                  className={arClass(
                    `text-lg font-bold max-sm:text-base ${
                      darkMode ? "text-white" : "text-slate-900"
                    }`
                  )}
                >
                  <Text>Amount Breakdown</Text>
                </h4>
              </div>

              <div className={arClass("space-y-4 max-sm:space-y-3")}>
                {/* Amount Input */}
                <div
                  className={arClass(
                    `flex flex-col gap-4 p-4 rounded-lg max-sm:p-3 ${
                      darkMode ? "bg-slate-900/50" : "bg-slate-50"
                    }`
                  )}
                >
                  <label
                    className={arClass(
                      `text-xs font-semibold mb-2 block max-sm:text-xss ${
                        darkMode ? "text-white" : "text-gray-800"
                      }`
                    )}
                  >
                    <Text>Original Amount</Text>
                  </label>
                  <div className={arClass("relative")}>
                    <span
                      className={arClass(
                        "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 max-sm:left-2"
                      )}
                    >
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={payment.amount || ""}
                      onChange={(e) =>
                        setPayment((s) => ({ ...s, amount: e.target.value }))
                      }
                      className={arClass(
                        `w-full pl-8 pr-4 py-3 px-2 rounded-lg border transition-colors max-sm:py-2 max-sm:pl-7 max-sm:text-xs ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 placeholder-gray-500"
                            : "bg-gray-50 border-gray-300 placeholder-gray-400"
                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20`
                      )}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div
                  className={arClass(
                    "grid grid-cols-2 gap-3 max-sm:gap-2 max-sm:grid-cols-1"
                  )}
                >
                  {/* Discount Input */}
                  <div
                    className={arClass(
                      `flex flex-col gap-4 p-3 rounded-lg max-sm:p-2 ${
                        darkMode ? "bg-slate-900/30" : "bg-slate-50/50"
                      }`
                    )}
                  >
                    <label
                      className={arClass(
                        `text-xs font-medium mb-2 block max-sm:text-xss ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`
                      )}
                    >
                      <Text>Discount (%)</Text>
                    </label>
                    <div className={arClass("relative")}>
                      <span
                        className={arClass(
                          "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 max-sm:left-2"
                        )}
                      >
                        <FaPercentage
                          size={12}
                          className="max-sm:w-2.5 max-sm:h-2.5"
                        />
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={payment.discountPercent || ""}
                        onChange={(e) => {
                          let val = Number(e.target.value);
                          if (val > 100) val = 100;
                          if (val < 0) val = 0;
                          setPayment((s) => ({
                            ...s,
                            discountPercent: val,
                          }));
                        }}
                        className={arClass(
                          `w-full pl-8 pr-4 py-3 px-2 rounded-lg border transition-colors max-sm:py-2 max-sm:pl-7 max-sm:text-xs ${
                            darkMode
                              ? "bg-gray-700 border-gray-600 placeholder-gray-500"
                              : "bg-gray-50 border-gray-300 placeholder-gray-400"
                          } focus:outline-none focus:ring-2 focus:ring-blue-500/20`
                        )}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Final Amount Display */}
                  <div
                    className={arClass(
                      `flex flex-col gap-4 p-3 rounded-lg max-sm:p-2 ${
                        darkMode ? "bg-slate-900/30" : "bg-slate-50/50"
                      }`
                    )}
                  >
                    <div
                      className={arClass(
                        `text-xs font-medium mb-2 block max-sm:text-xss ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`
                      )}
                    >
                      <Text>Final Amount</Text>
                    </div>
                    <div
                      className={arClass(
                        `w-full pl-8 pr-4 py-3 px-2 rounded-lg border transition-colors max-sm:py-2 max-sm:pl-7 max-sm:text-xs ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 placeholder-gray-500"
                            : "bg-gray-50 border-gray-300 placeholder-gray-400"
                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20`
                      )}
                    >
                      ${amountAfterDiscount}
                    </div>
                  </div>
                </div>
              </div>
              {/* Notes Input */}
              <div className={arClass("flex flex-col gap-4 max-sm:gap-3")}>
                <label
                  className={arClass(
                    `text-xs font-medium block max-sm:text-xss ${
                      darkMode ? "text-white" : "text-gray-800"
                    }`
                  )}
                >
                  <Text>Notes</Text>
                </label>
                <textarea
                  value={payment.notes || ""}
                  onChange={(e) =>
                    setPayment((s) => ({ ...s, notes: e.target.value }))
                  }
                  rows={3}
                  className={arClass(
                    `w-full px-3 py-2 rounded-lg border transition-colors max-sm:text-xs ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 placeholder-gray-500"
                        : "bg-gray-50 border-gray-300 placeholder-gray-400"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`
                  )}
                  placeholder={textTranslator(
                    "Add any payment notes...",
                    "PAYMENTS"
                  )}
                />
              </div>
            </motion.div>

            {/* Payment Info Section */}
            <motion.div
              whileHover={{ y: -5 }}
              className={arClass(
    `p-6 rounded-xl max-sm:p-4 max-sm:mb-4 w-full flex-1 ${
      darkMode
        ? "bg-slate-800/50 border border-slate-700/50"
        : "bg-white/50 border border-slate-200"
    } backdrop-blur-sm`
  )}
>
            
              <div
                className={arClass("flex items-center gap-3 mb-4 max-sm:mb-3")}
              >
                <FaTag
                  className={arClass(
                    `w-5 h-5 max-sm:w-4 max-sm:h-4 ${statusConfig.accent}`
                  )}
                />
                <h4
                  className={arClass(
                    `text-lg font-bold max-sm:text-base ${
                      darkMode ? "text-white" : "text-slate-900"
                    }`
                  )}
                >
                  <Text>Payment Status & Method</Text>
                </h4>
              </div>

              <div className={arClass("space-y-4 max-sm:space-y-3")}>
                {/* Payment Method Selection */}
                <div className={arClass("flex flex-col gap-4 max-sm:gap-3")}>
                  <label
                    className={arClass(
                      `text-xs font-medium block max-sm:text-xss ${
                        darkMode ? "text-white" : "text-gray-800"
                      }`
                    )}
                  >
                    <Text>Payment Method</Text>
                  </label>
                  <div
                    className={arClass(
                      "grid grid-cols-2 gap-2 max-sm:grid-cols-1 max-sm:gap-2"
                    )}
                  >
                    {paymentOptions.map((method) => {
                      const MethodIcon = method.icon;
                      const isSelected = payment.method === method.value;
                      return (
                        <motion.button
                          key={method.value}
                          type="button"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() =>
                            setPayment((s) => ({ ...s, method: method.value }))
                          }
                          className={arClass(
                            `p-3 rounded-lg border transition-all flex items-center gap-2 max-sm:p-2 max-sm:justify-start ${
                              isSelected
                                ? darkMode
                                  ? "border-blue-500 bg-blue-900/30"
                                  : "border-blue-500 bg-blue-100"
                                : darkMode
                                ? "border-slate-600 bg-slate-800/50 hover:border-slate-500"
                                : "border-gray-300 bg-white hover:border-gray-400"
                            }`
                          )}
                        >
                          <MethodIcon
                            className={arClass(
                              `max-sm:w-4 max-sm:h-4 ${
                                isSelected
                                  ? "text-blue-500"
                                  : darkMode
                                  ? "text-slate-400"
                                  : "text-gray-500"
                              }`
                            )}
                          />
                          <span
                            className={arClass(
                              `text-sm font-medium max-sm:text-xs truncate ${
                                isSelected
                                  ? "text-blue-600 dark:text-blue-400"
                                  : darkMode
                                  ? "text-slate-300"
                                  : "text-gray-700"
                              }`
                            )}
                          >
                            {method.label}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                {/* Payment Status Selection */}
                <div className={arClass("flex flex-col gap-4 max-sm:gap-3")}>
                  <label
                    className={arClass(
                      `text-xs font-medium block max-sm:text-xss ${
                        darkMode ? "text-white" : "text-gray-800"
                      }`
                    )}
                  >
                    <Text>Payment Status</Text>
                  </label>
                  <div
                    className={arClass(
                      "grid grid-cols-3 gap-2 max-sm:grid-cols-2 max-sm:gap-2"
                    )}
                  >
                    {paymentStatusOptions.map((status) => {
                      const StatusIcon = status.icon;
                      const isSelected = payment.paymentStatus === status.value;
                      const statusConfig = getStatusConfig(status.value);
                      return (
                        <motion.button
                          key={status.value}
                          type="button"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() =>
                            setPayment((s) => ({
                              ...s,
                              paymentStatus: status.value,
                            }))
                          }
                          className={arClass(
                            `flex-1 p-3 rounded-lg border transition-all flex items-center justify-center gap-2 max-sm:p-2 ${
                              isSelected
                                ? `${statusConfig.border} ${statusConfig.bg}`
                                : darkMode
                                ? "border-slate-600 bg-slate-800/50 hover:border-slate-500"
                                : "border-gray-300 bg-white hover:border-gray-400"
                            }`
                          )}
                        >
                          <StatusIcon
                            className={arClass(
                              `max-sm:w-3.5 max-sm:h-3.5 ${
                                isSelected
                                  ? statusConfig.accent
                                  : darkMode
                                  ? "text-slate-400"
                                  : "text-gray-500"
                              }`
                            )}
                          />
                          <span
                            className={arClass(
                              `text-sm font-medium max-sm:text-xs truncate ${
                                isSelected
                                  ? statusConfig.text
                                  : darkMode
                                  ? "text-slate-300"
                                  : "text-gray-700"
                              }`
                            )}
                          >
                            {status.label}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentDetails;
